import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSubmissionsStart, fetchSubmissionsSuccess, fetchSubmissionsFailure } from "../../../../Redux/slices/oaapSlice";
import { listOaapApi } from "../../../../services/oaap/uploadOaapApi";
import { Card, CardContent, Typography, Box, Chip, LinearProgress, Button } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import Loader from "@features/shared/components/Loader";
import Message from "@features/shared/components/Message";

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const CARD_PALETTE = [
  { border: "#4f46e5", bg: "#eef2ff", divider: "#e0e7ff", chip: "#eef2ff", chipText: "#4f46e5", iconBg: "#e0e7ff", iconColor: "#4f46e5", btnBg: "#4f46e5", btnHover: "#4338ca" },
  { border: "#16a34a", bg: "#f0fdf4", divider: "#dcfce7", chip: "#dcfce7", chipText: "#15803d", iconBg: "#dcfce7", iconColor: "#16a34a", btnBg: "#16a34a", btnHover: "#15803d" },
  { border: "#7c3aed", bg: "#f5f3ff", divider: "#ede9fe", chip: "#ede9fe", chipText: "#6d28d9", iconBg: "#ede9fe", iconColor: "#7c3aed", btnBg: "#7c3aed", btnHover: "#6d28d9" },
];

const ratingColor = (r) => {
  if (r <= 4) return "#e60000";
  if (r <= 6) return "#FF9500";
  return "#34C759";
};

const statusLabel = (r) => {
  if (r <= 4) return "Needs Improvement";
  if (r <= 6) return "Satisfactory";
  return "Good";
};

const ScoreBar = ({ rating }) => {
  const color = ratingColor(rating);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }}>
      <Typography sx={{ fontSize: "2rem", fontWeight: 700, color, lineHeight: 1 }}>
        {rating}
      </Typography>
      <Typography sx={{ fontSize: "1rem", color: "#aaa", lineHeight: 1 }}>/ 10</Typography>
      <Box sx={{ flex: 1 }}>
        <LinearProgress
          variant="determinate"
          value={rating * 10}
          sx={{
            height: 8, borderRadius: 4,
            backgroundColor: "#eee",
            "& .MuiLinearProgress-bar": { backgroundColor: color, borderRadius: 4 },
          }}
        />
      </Box>
    </Box>
  );
};

const BMOAAPList = ({ userCode, selectedMonth, selectedYear, onViewFeedback }) => {
  const dispatch = useDispatch();
  const { submissions, submissionsLoading, submissionsError } = useSelector((s) => s.oaap);

  useEffect(() => {
    if (!userCode) return;
    dispatch(fetchSubmissionsStart());
    listOaapApi(userCode)
      .then((res) => dispatch(fetchSubmissionsSuccess(res.data)))
      .catch((err) => dispatch(fetchSubmissionsFailure(err?.response?.data?.msg || "Failed to load submissions")));
  }, [dispatch, userCode]);

  if (submissionsLoading) return <Loader loading />;
  if (submissionsError) return <Message message={submissionsError} />;
  if (submissions.length === 0) return <Message message="No OAAP submissions found." />;

  const latestByMonth = Object.values(
    submissions.reduce((acc, sub) => {
      const key = `${sub.year}-${sub.month}`;
      if (!acc[key] || sub.version > acc[key].version) acc[key] = sub;
      return acc;
    }, {})
  ).sort((a, b) => b.year - a.year || b.month - a.month);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {latestByMonth.map((sub, idx) => {
        const hasFeedback = sub.rating !== null && sub.rating !== undefined;
        const submittedDate = new Date(sub.submission_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        const pal = CARD_PALETTE[idx % CARD_PALETTE.length];

        return (
          <Card
            key={sub.id}
            elevation={0}
            sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", overflow: "hidden", borderLeft: `4px solid ${pal.border}` }}
          >
            <CardContent sx={{ p: "20px !important" }}>
              {/* Header: Month + Version */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, pb: 2, borderBottom: `1px solid ${pal.divider}`, backgroundColor: pal.bg, borderRadius: 1.5, px: 1.5, pt: 1.5, mx: -0.5 }}>
                <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: "#1a1a1a" }}>
                  {MONTH_NAMES[sub.month - 1]} {sub.year}
                </Typography>
                <Chip
                  label={`Version ${sub.version}`}
                  size="small"
                  sx={{ backgroundColor: pal.chip, color: pal.chipText, fontWeight: 600, fontSize: "0.75rem", borderRadius: 2 }}
                />
              </Box>

              {/* File row */}
              <Box
                sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  backgroundColor: "#f7f7f7", borderRadius: 2, px: 2, py: 1.5, mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 1.5, backgroundColor: pal.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <DescriptionOutlinedIcon sx={{ color: pal.iconColor, fontSize: 22 }} />
                  </Box>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 500, color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={sub.original_file_name}>
                    {sub.original_file_name}
                  </Typography>
                </Box>
                {sub.file_path && (
                  <Button
                    component="a"
                    href={sub.file_path}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    size="small"
                    startIcon={<FileDownloadOutlinedIcon />}
                    sx={{
                      ml: 1.5, flexShrink: 0, borderRadius: 2,
                      backgroundColor: pal.btnBg, textTransform: "none", fontWeight: 600,
                      fontSize: "0.85rem", px: 2, py: 0.75,
                      "&:hover": { backgroundColor: pal.btnHover },
                    }}
                  >
                    Download
                  </Button>
                )}
              </Box>

              {/* Feedback / pending section */}
              {hasFeedback ? (
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                    <Typography sx={{ fontSize: "0.9rem", color: "#666", fontWeight: 500 }}>Score</Typography>
                    <Chip
                      icon={<StarOutlineIcon sx={{ fontSize: "14px !important" }} />}
                      label={statusLabel(sub.rating)}
                      size="small"
                      sx={{
                        backgroundColor: "#FFF3E0", color: ratingColor(sub.rating),
                        fontWeight: 700, fontSize: "0.78rem", borderRadius: 3,
                        "& .MuiChip-icon": { color: ratingColor(sub.rating) },
                      }}
                    />
                  </Box>
                  <ScoreBar rating={sub.rating} />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    border: "1.5px solid #FFD58C", borderRadius: 2, px: 2, py: 1.5,
                    backgroundColor: "#FFFBF0",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, backgroundColor: "#FFE9C0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <AccessTimeOutlinedIcon sx={{ color: "#E65C00", fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#E65C00" }}>Awaiting ZM Review</Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "#B35900" }}>Score and feedback not submitted yet</Typography>
                    </Box>
                  </Box>
                  <Chip label="PENDING" size="small" sx={{ backgroundColor: "#FFD58C", color: "#7A3B00", fontWeight: 700, fontSize: "0.7rem" }} />
                </Box>
              )}

              {/* Footer row */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, pt: 1.5, borderTop: "1px solid #f0f0f0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: "#999" }} />
                  <Typography sx={{ fontSize: "0.8rem", color: "#666" }}>Submitted On: {submittedDate}</Typography>
                </Box>
                {hasFeedback && (
                  <Button
                    onClick={() => onViewFeedback(sub)}
                    variant="contained"
                    size="small"
                    sx={{
                      borderRadius: 2, backgroundColor: pal.chip, color: pal.chipText,
                      fontWeight: 600, fontSize: "0.8rem", textTransform: "none", px: 1.75, boxShadow: "none",
                      "&:hover": { backgroundColor: pal.divider, boxShadow: "none" },
                    }}
                  >
                    View ZM Feedback
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

export default BMOAAPList;
